/**
 * WebSocket frames, discriminated on `type`.
 *
 * Keepalive is the bare text `ping` and `pong`, not JSON, so the Durable Object
 * auto-response can answer without waking a hibernated object. It has no schema.
 *
 * Forward compatibility: clients ignore a server frame whose `type` they do not
 * know (`isRelayServerFrameType` below). The server validates every frame it
 * creates and every client frame it receives strictly.
 *
 * Claims are not frames. A Reset offer is claimed against core-api
 * (`reset.ts`), because first-writer-wins needs durable state and the Relay
 * has none.
 */
import { z } from "zod";
import { RelayEnvelope } from "./envelope.js";
import { RelayConnectionId, RelayEventId, RelayTimestamp } from "./primitives.js";
import { RelaySurface } from "./topics.js";
export const RelayReconnectReason = z.enum(["deploy", "policy"]);
/**
 * How long a client waits, uniformly at random, before reconnecting after a
 * `reconnect` frame or a dropped socket. A Worker deploy closes every socket at
 * once, so at 25,000 members a 2-second window is 12,500 upgrades a second;
 * 10 seconds keeps the storm and the Keycloak JWKS fetches behind it inside
 * what one deploy should cost.
 */
export const RELAY_RECONNECT_JITTER_MAX_SECONDS = 10;
/**
 * Close code the inbox uses when it drops a socket whose token has expired
 * (TRD section 7). The client refreshes its token and reconnects at once
 * instead of backing off. Private-use range per RFC 6455.
 */
export const RELAY_CLOSE_CODE_REAUTHENTICATE = 4401;
/**
 * Close code for a socket that stopped answering the keepalive but never sent
 * a close. The client treats it like any other drop.
 */
export const RELAY_CLOSE_CODE_STALE = 4408;
/**
 * One of the member's live connections, as the inbox describes it to every
 * other. Identifiers and timing only: no device name, no token claims.
 *
 * `primary` is a label the member's clients use to decide which one of them
 * displays a Reset offer; the Worker still delivers every event to every
 * socket and reads nothing into the label. The inbox sets it to the newest
 * connection by default and to whatever a client last asked for with
 * `set_primary`; when the primary leaves, the newest remaining connection
 * takes it. Exactly one connection is primary whenever any is connected.
 */
export const RelayPeer = z.strictObject({
    connectionId: RelayConnectionId,
    client: RelaySurface,
    connectedAt: RelayTimestamp,
    primary: z.boolean(),
});
const PeerList = z
    .array(RelayPeer)
    .min(1)
    .refine((peers) => new Set(peers.map((peer) => peer.connectionId)).size === peers.length, {
    message: "duplicate connectionId",
})
    .refine((peers) => peers.filter((peer) => peer.primary).length === 1, {
    message: "exactly one connection is primary",
});
/**
 * The first frame on every connection: this socket's own `connectionId` and
 * the member's live connections, this one included, so a client knows at once
 * whether it is alone and whether it is the primary. There is no topic list
 * (there is no subscription), no sequence and no epoch (there is nothing to
 * resume). The client's next step is one fetch of its undelivered events from
 * core-api (`undelivered.ts`).
 */
export const RelayHelloFrame = z
    .strictObject({
    type: z.literal("hello"),
    connectionId: RelayConnectionId,
    peers: PeerList,
})
    .refine((hello) => hello.peers.some((peer) => peer.connectionId === hello.connectionId), {
    message: "peers must include this connection",
    path: ["peers"],
});
/**
 * The member's live connections changed: one attached, one left, or a client
 * moved the primary label. Sent to every remaining socket with the full
 * current list, so a client never reconciles deltas.
 */
export const RelayPeersFrame = z.strictObject({ type: z.literal("peers"), peers: PeerList });
export const RelayEventFrame = z.strictObject({ type: z.literal("event"), event: RelayEnvelope });
export const RelayServerFrame = z.discriminatedUnion("type", [
    RelayHelloFrame,
    RelayEventFrame,
    RelayPeersFrame,
    z.strictObject({ type: z.literal("reconnect"), reason: RelayReconnectReason }),
]);
/**
 * A client has received one event frame. The
 * inbox resolves the pending `deliver()` for that event; the producer then
 * deletes the row it stored ahead for that surface, and the row of any surface
 * that does not answer in time stays for the catch-up fetch.
 *
 * Ack means received, not acted on. Every socket receives every topic, so a
 * client acknowledges frames for topics it does not handle too (Web and
 * `reset.offer`), otherwise every such publish would wait out the ack timeout
 * for it. For a topic it does handle, it acknowledges after applying the frame,
 * never before, and treats a redelivery of an acknowledged event as a no-op.
 * There is no presence frame: a connected socket proves presence by answering the `ping`
 * auto-response, and the inbox reads that timestamp when core-api asks.
 */
export const RelayAckFrame = z.strictObject({ type: z.literal("ack"), eventId: RelayEventId });
/**
 * A client asks the inbox to label one of the member's connections (its own,
 * or a peer the member picked from a prompt) as the primary. The inbox flags
 * that connection, clears the others and sends `peers` to every socket. An
 * unknown or closed `connectionId` is ignored.
 */
export const RelaySetPrimaryFrame = z.strictObject({
    type: z.literal("set_primary"),
    connectionId: RelayConnectionId,
});
export const RelayClientFrame = z.discriminatedUnion("type", [RelayAckFrame, RelaySetPrimaryFrame]);
const SERVER_FRAME_TYPES = new Set(RelayServerFrame.options.map((option) => option.shape.type.value));
/** True when a parsed JSON frame names a type this client version understands. */
export function isRelayServerFrameType(frame) {
    return (typeof frame === "object" &&
        frame !== null &&
        "type" in frame &&
        typeof frame.type === "string" &&
        SERVER_FRAME_TYPES.has(frame.type));
}
//# sourceMappingURL=frames.js.map